/**
 * The mission run page's model (missions vision, section 3): which phase a mission is in, what
 * the landing queue says about its branch, the facts in the header strip and the one line that
 * says what is happening now. Pure, so every phase is tested (__tests__/run.test.ts).
 */
import { failedOnModel, followUpCount, followUpKindOf, integrationNext, orgusLandingDue, orgusLands, sessionFailureText } from '@orgus/protocol';
import type { Landing, Mission, MissionLaunch, MissionSessionView, MissionView } from '@orgus/protocol';
import type { MissionStage, StageExtra, StageId, StageState } from '../stages.ts';
import { summaryLine } from '../../../lib/plainText.ts';

/**
 * planned: never started. preparing: a launch is out, no session yet. working: a session works.
 * needs: a session waits on a person. blocked: a decided request stopped a session. checking: the
 * landing queue checks the branch. review: finished, a person judges it. landing: checks passed,
 * the branch is on its way to main. landed: on main. done: closed as done without landing.
 * failed: a start, a session or a check failed. stopped: closed as stopped.
 */
export type RunPhase = 'planned' | 'preparing' | 'working' | 'needs' | 'blocked' | 'checking' | 'review' | 'landing' | 'landed' | 'done' | 'failed' | 'stopped';

/** The phases a person has to act on, in the header's primary action. */
export const RUN_PHASE_LABEL: Record<RunPhase, string> = {
  planned: 'Planned',
  preparing: 'Preparing',
  working: 'Working',
  needs: 'Needs you',
  blocked: 'Blocked',
  checking: 'Checking',
  review: 'In review',
  landing: 'Landing',
  landed: 'Landed',
  done: 'Done',
  failed: 'Failed',
  stopped: 'Stopped',
};

/** Phases whose clock has stopped: the agent's work is over (checks and landing are not its time). */
export function isSettled(p: RunPhase): boolean {
  return p !== 'preparing' && p !== 'working' && p !== 'needs' && p !== 'blocked';
}

/** The newest landing submitted for this mission (cancelled ones only when nothing else exists). */
export function missionLanding(landings: readonly Landing[] | null | undefined, missionId: string): Landing | null {
  const mine = (landings ?? []).filter((l) => l.missionId === missionId).sort((a, b) => b.submittedAt - a.submittedAt);
  return mine.find((l) => l.status !== 'cancelled') ?? mine[0] ?? null;
}

/** A person sent the branch to land (an agent's own submission is not a review). */
export function reviewedBy(l: Landing | null): boolean {
  return !!l && l.status !== 'cancelled' && !l.submittedBy.startsWith('agent:') && l.submittedBy !== 'landing-queue';
}

/** What the landing queue says about the branch's checks, for missionStages. */
export function checksOf(l: Landing | null): StageExtra['checks'] {
  if (!l || l.status === 'cancelled') return null;
  if (l.status === 'landed' || l.stage === 'push' || l.stage === 'deploy' || l.stage === 'landed') return 'passed';
  if (l.status === 'failed' || l.status === 'parked') return 'failed';
  return 'running';
}

const stageOf = (stages: readonly MissionStage[], id: StageId): StageState => stages.find((s) => s.id === id)?.state ?? 'waiting';

export function runPhase(m: Pick<Mission, 'closed'> & Partial<Pick<Mission, 'landed' | 'launches' | 'integration' | 'paused'>>, v: Pick<MissionView, 'state' | 'sessions' | 'blockers'>, stages: readonly MissionStage[], landing: Landing | null, now = Date.now()): RunPhase {
  if (m.closed?.as === 'stopped' || v.state === 'stopped') return 'stopped';
  if (m.landed || landing?.status === 'landed') return 'landed';
  if (m.closed?.as === 'done') return 'done';
  if (v.state === 'planned' && stageOf(stages, 'prepare') === 'waiting') return 'planned';
  if (stageOf(stages, 'prepare') === 'failed' || stageOf(stages, 'work') === 'failed' || stageOf(stages, 'check') === 'failed' || landing?.status === 'failed') return 'failed';
  if (v.state === 'waiting') return v.blockers.some((b) => b.kind === 'approval' && !!b.outcome && b.phase !== 'resuming') ? 'blocked' : 'needs';
  if (stageOf(stages, 'prepare') === 'current') return 'preparing';
  if (stageOf(stages, 'work') === 'current') return 'working';
  if (landing && (landing.status === 'queued' || landing.status === 'active')) return landing.stage === 'push' || landing.stage === 'deploy' ? 'landing' : 'checking';
  // QA-56: finished work Orgus lands by itself is landing, not a review a person must take first
  // (an unmet report fails the mission instead, and Orgus does not land it).
  if (v.state === 'done' && orgusLandingDue({ ...m, launches: m.launches ?? [] }, now)) return 'landing';
  return 'review';
}

/** The launch the page reads its facts from: the newest that started, else the newest. */
export function lastLaunch(m: Pick<Mission, 'launches'>): MissionLaunch | null {
  const ok = m.launches.filter((l) => l.result === 'ok');
  return ok[ok.length - 1] ?? m.launches[m.launches.length - 1] ?? null;
}

/**
 * The session the page follows: one waiting on a person first, then the newest live one, then
 * the newest seen at all.
 */
export function focusOf(v: Pick<MissionView, 'sessions'>): MissionSessionView | null {
  const seen = v.sessions.filter((s) => s.session);
  const byActivity = (a: MissionSessionView, b: MissionSessionView) => (b.session?.lastActivity ?? 0) - (a.session?.lastActivity ?? 0);
  return (
    seen.find((s) => s.state === 'waiting_for_human') ??
    [...seen].filter((s) => s.session!.status !== 'ended').sort(byActivity)[0] ??
    [...seen].sort(byActivity)[0] ??
    v.sessions[v.sessions.length - 1] ??
    null
  );
}

/** When the run started and, once it settled, when it stopped: the Elapsed fact. */
export function runSpan(m: Pick<Mission, 'launches' | 'closed'> & Partial<Pick<Mission, 'landed'>>, v: Pick<MissionView, 'sessions'>, phase: RunPhase): { from: number; to: number | null } | null {
  const first = m.launches.find((l) => l.result === 'ok') ?? m.launches[0];
  if (!first) return null;
  if (!isSettled(phase)) return { from: first.at, to: null };
  const last = Math.max(first.at, ...v.sessions.map((s) => s.session?.lastActivity ?? s.launch?.at ?? 0));
  return { from: first.at, to: m.landed?.at ?? m.closed?.at ?? last };
}

/** The branch the mission works on: the launch's own, else its checkout's, else where it landed. */
export function runBranch(m: Pick<Mission, 'launches'> & Partial<Pick<Mission, 'landed'>>, checkoutBranch: string | null | undefined, landing: Landing | null): string | null {
  const l = lastLaunch(m);
  return l?.branch ?? landing?.source.branch ?? m.landed?.branch ?? checkoutBranch ?? null;
}

export type NowTone = 'working' | 'attention' | 'danger' | 'success' | 'neutral';

/** The one bright line at the top of the stream: what is happening now, and its tone. */
export function nowLine(phase: RunPhase, ctx: { m: Pick<Mission, 'launches'> & Partial<Pick<Mission, 'landed' | 'closed' | 'report'>>; v: Pick<MissionView, 'sessions' | 'blockers' | 'pendingApprovals'> & Partial<Pick<MissionView, 'verdict'>>; focus: MissionSessionView | null; landing: Landing | null; machineName: string }): { text: string; tone: NowTone } {
  const { m, v, focus, landing, machineName } = ctx;
  const last = summaryLine(focus?.session?.lastText ?? '');
  switch (phase) {
    case 'planned':
      return { text: 'Not started', tone: 'neutral' };
    case 'preparing':
      return { text: `Starting the agent on ${machineName}`, tone: 'working' };
    case 'working':
      return { text: last || 'Working', tone: 'working' };
    case 'needs': {
      const a = v.pendingApprovals[0];
      if (a) return { text: a.title, tone: 'attention' };
      const b = v.blockers.find((x) => x.kind === 'waiting' || x.kind === 'approval');
      return { text: last || b?.title || 'The agent is waiting for your answer', tone: 'attention' };
    }
    case 'blocked':
      // The card below tells the whole story; this line names it.
      const title = v.blockers.find((b) => b.kind === 'approval')?.title;
      return { text: title ? title.split(/\.\s/)[0]! : 'A request stopped the session', tone: 'danger' };
    case 'checking':
      return { text: landing?.step || 'Checking the branch', tone: 'working' };
    case 'landing':
      return { text: landing?.step || 'Landing the branch', tone: 'working' };
    case 'review':
      // ADR 0078: Orgus closes landed, verified work itself; this says why a person is needed.
      return { text: ctx.v.verdict?.verdict === 'met' ? 'Finished. Every criterion met, the branch has not landed' : 'Finished. Orgus could not confirm every criterion', tone: 'neutral' };
    case 'landed': {
      const branch = m.landed?.branch ?? landing?.source.branch;
      const sha = m.landed?.sha ?? landing?.landedSha;
      const landedText = branch ? `Landed ${branch}${sha ? ` as ${sha.slice(0, 7)}` : ''}` : 'Landed';
      // QA-62: a step the agent left for a person stays in view after the rest landed.
      const person = m.report?.needsPerson?.[0];
      if (person) return { text: `${landedText}. Left for a person: ${person}`, tone: 'attention' };
      return { text: landedText, tone: 'success' };
    }
    case 'done':
      return { text: 'Accepted and closed as done', tone: 'neutral' };
    case 'stopped':
      return { text: 'Stopped', tone: 'neutral' };
    case 'failed':
      return { text: failureLine({ m, v, landing }), tone: 'danger' };
  }
}

/** The failure in one line, naming what failed: the check, the start or the session. */
export function failureLine({ m, v, landing }: { m: Pick<Mission, 'launches'>; v: Pick<MissionView, 'sessions' | 'blockers'>; landing: Landing | null }): string {
  if (landing && (landing.status === 'failed' || landing.status === 'parked')) {
    const failing = [...landing.events].reverse().find((e) => e.tone === 'error' || e.tone === 'warn');
    return landing.problem?.what ? `${landing.problem.what}${landing.problem.why ? ` ${landing.problem.why}` : ''}` : (failing?.text ?? 'The checks failed');
  }
  // ADR 0078: it ended without finishing, was interrupted, or never reported: that blocker says why.
  const ended = v.blockers.find((b) => b.kind === 'unmet' || b.kind === 'interrupted' || b.kind === 'unseen');
  if (ended) return ended.title;
  const failed = [...v.sessions].reverse().find((s) => s.state === 'failed');
  if (failed?.session) return sessionFailureText(failed.session, failed.launch?.model ?? null);
  const launch = [...m.launches].reverse().find((l) => l.result === 'error');
  if (launch) return launch.message ? `Did not start: ${launch.message}` : 'The agent did not start';
  return v.blockers.find((b) => b.kind === 'session_failed' || b.kind === 'launch_failed')?.title ?? 'The mission failed';
}

/** What a retry changes, in a few words, or null when it repeats the last start as it was. */
export function retryChange(v: Pick<MissionView, 'sessions'>): string | null {
  return v.sessions.some((s) => s.state === 'failed' && s.session && failedOnModel(s.session)) ? 'Retries with a model this account can use' : null;
}

/** What failed, for the failure row's actions: the landing checks, a session, or the start itself. */
export type FailureKind = 'check' | 'session' | 'start';

export function failureKind({ m, v, landing }: { m: Pick<Mission, 'launches'>; v: Pick<MissionView, 'sessions'>; landing: Landing | null }): FailureKind {
  if (landing && (landing.status === 'failed' || landing.status === 'parked')) return 'check';
  if (v.sessions.some((s) => s.state === 'failed' && s.session)) return 'session';
  return m.launches.some((l) => l.result === 'error') ? 'start' : 'session';
}

/**
 * A finished mission whose branch Orgus could not land (QA-59): the header chip, the now line and
 * who acts next, from the integration record (ADR 0086 phase 1). Null when nothing stopped it.
 * person: a person has to act, so the page asks for one; otherwise Orgus's agent is on it.
 */
export interface IntegrationSetback {
  label: string;
  text: string;
  who: string;
  person: boolean;
}

export function integrationSetback(m: Pick<Mission, 'launches' | 'closed'> & Partial<Pick<Mission, 'landed' | 'integration'>>): IntegrationSetback | null {
  const rec = m.integration;
  if (!rec || m.landed || m.closed) return null;
  if (rec.status === 'stopping' || rec.status === 'running' || rec.status === 'submitted' || rec.status === 'landed') return null;
  const kind = followUpKindOf(rec.status);
  const drives = orgusLands(m);
  const next = integrationNext(rec, kind && drives ? { followUps: followUpCount(m, kind) } : {});
  if (!next || next.who === 'orgus' || next.who === 'landing') return null;
  const files = (rec.conflicts ?? []).slice(0, 3).join(', ');
  const label = rec.status === 'conflict' ? 'Merge conflict' : rec.status === 'check-failed' ? 'Checks failed' : 'Not landed';
  const text = rec.status === 'conflict' ? `Merge conflict in ${files || 'the branch'}` : rec.status === 'check-failed' ? 'The checks failed on the merged branch' : summaryLine(rec.message ?? '') || 'Orgus could not land the branch';
  const fix = rec.status === 'conflict' ? 'resolve it' : 'fix them';
  const started = m.launches.some((l) => l.followUp?.fromLaunchId === rec.launchId && l.result !== 'error');
  if (started) return { label, text, who: `Orgus started an agent to ${fix}`, person: false };
  if (next.who === 'agent' && kind && drives) return { label, text, who: `Orgus is starting an agent to ${fix}`, person: false };
  return { label, text, who: 'Needs a person', person: true };
}
